import React from "react";
import { ShieldCheck, Cpu, Radio, Activity } from "lucide-react";

export default function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="relative flex min-h-screen">
      {/* Left panel — Brand */}
      <div
        className="relative hidden overflow-hidden border-r border-stone-200 text-slate-900 lg:flex lg:w-[45%] flex-col justify-between p-10"
        style={{
          background: "linear-gradient(180deg, #ffffff 0%, #f5f5f4 100%)",
        }}
      >
        {/* Decorative circles */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-stone-300/30 blur-3xl" />
          <div className="absolute bottom-10 -left-10 h-48 w-48 rounded-full bg-slate-200/40 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-stone-200/40 blur-3xl" />
        </div>

        {/* Top — Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-stone-600 to-slate-700 text-white shadow-lg shadow-slate-300/40">
            <ShieldCheck size={20} strokeWidth={2} />
          </div>
          <div>
            <p className="font-display text-lg font-bold tracking-wide">Reyansh Technologies</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Safety & Emergency Response</p>
          </div>
        </div>

        {/* Center — Tagline */}
        <div className="relative z-10 space-y-6">
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight">
            AI-Driven Vehicle<br />
            Safety & Crash<br />
            Detection System
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-slate-600">
            Multi-service emergency console for coordinating Police, Fire, Ambulance, 
            and Hospital response — powered by real-time IoT telemetry and GNSS positioning.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 pt-2">
            {[
              { icon: Cpu, label: "IoT Edge" },
              { icon: Radio, label: "Live Dispatch" },
              { icon: Activity, label: "Multi-Sensor" },
            ].map((f) => (
              <span key={f.label} className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-600">
                <f.icon size={12} strokeWidth={2} /> {f.label}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <p className="relative z-10 text-[11px] font-medium uppercase tracking-[0.15em] text-slate-400">
          © 2026 Reyansh Technologies · Rajkot, India
        </p>
      </div>

      {/* Right panel — Form */}
      <div className="flex flex-1 flex-col bg-stone-100">
        {/* Mobile logo */}
        <div className="flex items-center gap-3 px-6 py-5 lg:hidden">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-stone-600 to-slate-700 text-white shadow-sm">
            <ShieldCheck size={18} strokeWidth={2} />
          </div>
          <p className="font-display text-[15px] font-bold text-slate-900">Reyansh Technologies</p>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-8">
          <div className="w-full max-w-[420px]">
            <div className="rounded-2xl border border-stone-200 bg-white p-7 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.08)] sm:p-9">
              <div className="mb-6 space-y-1">
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
                {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
              </div>
              {children}
            </div>
            {footer && <div className="mt-5 text-center text-sm text-slate-500">{footer}</div>}
          </div>
        </div>

        <div className="hidden lg:block text-center pb-5 text-[11px] font-medium uppercase tracking-widest text-slate-400">
          Precision · Reliability · Safety
        </div>
      </div>
    </div>
  );
}
